import React, { useEffect, useState } from "react";
import styles from "../BulkUploadTabs.module.css";
import { FaEye } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import {
  getBulkUploadFilter,
  getUploadedExcelsList,
} from "../../../api/bulk-upload-api";
import { formatDate } from "../../../utils/dateFormatter";
import Pagination from "../../pagination/Pagination";
import { useNavigate } from "react-router-dom";
import Button from "../../../component/button/Button";
import { IoIosArrowDown } from "react-icons/io";
import InputText from "../../../component/inputText/InputText";

const UploadHistory = () => {
  const [tableData, setTableData] = useState([]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dropDownData, setDropDownData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  // True until the first fetch resolves, so the empty state below doesn't
  // flash "Result Not Found" before any data has loaded.
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    dateAdded: "",
  });

  const totalPages = Math.ceil(
    (totalCount || tableData[0]?.total) / rowsPerPage
  );

  // Fetch filter options
  useEffect(() => {
    const filterData = async () => {
      const response = await getBulkUploadFilter();
      setDropDownData(response.data);
    };
    filterData();
  }, []);

  // Handle invalid current page
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setRowsPerPage(pageSize);
  };

  // Fetch data with current filters
  const getData = async () => {
    const { status, type, dateAdded } = filters;

    try {
      const response = await getUploadedExcelsList(
        rowsPerPage,
        currentPage,
        status,
        type,
        dateAdded,
        searchTerm
      );

      setTableData(response.data?.data || []);
      setTotalCount(response.data?.total || 0);
    } finally {
      setLoading(false);
    }
  };

  // Search text or any dropdown filter narrows the list — used to pick the
  // empty-state wording ("Result Not Found" vs. "No uploads yet").
  const isFiltering =
    searchTerm.trim() !== "" || Object.values(filters).some((v) => v);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    getData();
  }, [rowsPerPage, currentPage, filters]);

  // Handle filter updates
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };
  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim() === "") {
        getData();
      } else {
        getData();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleViewDetails = (id) => {
    console.log(`View details for upload ID: ${id}`);
  };

  return (
    <div className={styles["history-wrapper"]}>
      <div className={styles["history-header"]}>
        <h2>Upload History</h2>
        <p>View and analyze past upload activities</p>
      </div>

      <div className={styles["history-controls"]}>
        <div className={styles["history-searchBox"]}>
          <IoSearchOutline className={styles["history-searchIcon"]} />
          <InputText
            type="text"
            placeholder="search by file name..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <Button
          iconRight={<IoIosArrowDown />}
          options={dropDownData?.data?.status}
          type="select"
          selectLabel="All status"
          variant="outline"
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        />

        <Button
          iconRight={<IoIosArrowDown />}
          options={dropDownData?.data?.types}
          type="select"
          selectLabel="All Types"
          variant="outline"
          value={filters.type}
          onChange={(e) => handleFilterChange("type", e.target.value)}
        />

        <Button
          iconRight={<IoIosArrowDown />}
          options={dropDownData?.data?.dataAdded}
          type="select"
          selectLabel="All Dates"
          variant="outline"
          value={filters.dateAdded}
          onChange={(e) => handleFilterChange("dateAdded", e.target.value)}
        />
      </div>

      <div className={styles["history-tableWrapper"]}>
        <table className={styles["history-table"]}>
          <thead>
            <tr>
              <th>Upload Details</th>
              <th>Status</th>
              <th>Items</th>
              <th>Success Rate</th>
              <th>Size</th>
              <th>Date</th>
              <th>User</th>
            </tr>
          </thead>
          <tbody>
            {tableData?.map((upload, idx) => (
              <tr key={idx}>
                <td>
                  <strong>{upload.original_filename}</strong>
                  <br />
                  <span>{formatDate(upload.created_at)}</span>
                </td>
                <td>
                  <span
                    className={`${styles["history-status"]} ${
                      styles[
                        `history-${upload.status
                          ?.toLowerCase()
                          ?.replace(/\s/g, "")}`
                      ]
                    }`}
                  >
                    {upload.status}
                  </span>
                </td>
                <td>
                  {upload.total_records}
                  {upload.failed && (
                    <div className={styles["history-failedItems"]}>
                      {upload.failed}
                    </div>
                  )}
                </td>
                <td>
                  <div className={styles["history-successBarWrapper"]}>
                    <strong>{upload.progress_percentage}%</strong>
                    <div className={styles["history-bar"]}>
                      <div
                        className={styles["history-fill"]}
                        style={{ width: `${upload.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className={styles.tableData}>
                  {upload.file_size_bytes
                    ? `${(upload.file_size_bytes / 1024).toFixed(2)} KB`
                    : "0 KB"}
                </td>
                <td className={styles.tableData}>
                  {formatDate(upload.estimated_completion_at) || "----"}
                </td>
                <td className={styles.tableData}>
                  {upload.user?.name || "Unknown"}
                </td>
              </tr>
            ))}
            {!loading && !tableData?.length && (
              <tr>
                <td colSpan={7} className={styles["history-empty"]}>
                  <strong>{isFiltering ? "Result Not Found" : "No uploads yet"}</strong>
                  <span>
                    {isFiltering
                      ? "No uploads match your search or filters. Try a different file name or clear the filters."
                      : "Files you upload with Bulk Upload will appear here."}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          onPageChange={handlePageChange}
          className={styles["history-pagination"]}
        />
      </div>
    </div>
  );
};

export default UploadHistory;
